import { useState } from 'react';
import emailValidator from 'email-validator';
import { TextField } from 'seagull';
import { routes } from "../../../constants";
import { useStoreActions } from "../../../contexts";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { Footer, FormTitle, FooterText, FormFields, FooterLink, ErrorMessage, FormContainer, FormDescription, SubmitButtonField, VerticalFormFields } from "../components/FormComponents";
import UnauthorizedPageContainer from "../components/UnauthorizedPageContainer";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SentScreen = _ref => {
  let {
    email,
    tryAgain
  } = _ref;
  return /*#__PURE__*/_jsx(UnauthorizedPageContainer, {
    testId: "reset-email-sent-msg",
    children: /*#__PURE__*/_jsxs(FormContainer, {
      children: [/*#__PURE__*/_jsx(FormTitle, {
        children: "Check Your Email"
      }), /*#__PURE__*/_jsxs(FormDescription, {
        "data-testid": "reset-password-sent-description",
        children: ["We've sent password reset instructions to ", email, ", Please open it and click the password reset link."]
      }), /*#__PURE__*/_jsxs(Footer, {
        children: [/*#__PURE__*/_jsx(FooterText, {
          children: "Didn't receive an email? "
        }), /*#__PURE__*/_jsx(FooterLink, {
          onClick: tryAgain,
          children: "Wrong email?"
        })]
      })]
    })
  });
};
const FormScreen = _ref2 => {
  let {
    onSuccess
  } = _ref2;
  const {
    api,
    logBI
  } = useStoreActions();
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailWasBlurred, setEmailWasBlurred] = useState(false);
  const [resetInProgress, setResetInProgress] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(null);
  const showEmailFieldError = email !== '' && !isEmailValid && emailWasBlurred;
  return /*#__PURE__*/_jsx(UnauthorizedPageContainer, {
    testId: "reset-password-form",
    children: /*#__PURE__*/_jsxs(FormContainer, {
      onChange: () => setResetPasswordError(null),
      onSubmit: async event => {
        logBI(10600, {});
        event.preventDefault();
        setResetInProgress(true);
        try {
          await api.auth.resetPassword(email);
          setResetInProgress(false);
          onSuccess(email);
        } catch (e) {
          setResetInProgress(false);
          setResetPasswordError('Failed to reset password, please try again');
        }
      },
      children: [/*#__PURE__*/_jsx(FormTitle, {
        children: "Reset Password"
      }), /*#__PURE__*/_jsx(FormDescription, {
        children: "Enter the email address associated with your account and we'll send you a link to reset your password"
      }), /*#__PURE__*/_jsx(FormFields, {
        children: /*#__PURE__*/_jsx(VerticalFormFields, {
          children: /*#__PURE__*/_jsx(TextField, {
            collapseHelperText: false,
            autoFocus: true,
            label: "Work email",
            type: "email",
            value: email,
            onChange: event => {
              const value = event.target.value;
              const isValid = emailValidator.validate(value);
              setEmail(value);
              setIsEmailValid(isValid);
            },
            error: showEmailFieldError,
            onBlur: () => setEmailWasBlurred(true),
            "data-testid": "email-input"
          })
        })
      }), /*#__PURE__*/_jsx(SubmitButtonField, {
        disabled: !isEmailValid,
        isLoading: resetInProgress,
        "data-testid": "reset-password-button",
        children: "Send a reset link"
      }), resetPasswordError && /*#__PURE__*/_jsx(ErrorMessage, {
        "data-testid": "error-msg",
        children: resetPasswordError
      }), /*#__PURE__*/_jsx(Footer, {
        children: /*#__PURE__*/_jsx(FooterLink, {
          toInternalLink: routes.login,
          "data-testid": "back-to-login-button",
          children: "Back to sign in"
        })
      })]
    })
  });
};
export function ResetPasswordScreen() {
  const [email, setEmail] = useState(null);
  useSetDocumentTitle('Anchor Reset Password');
  return email ? /*#__PURE__*/_jsx(SentScreen, {
    email: email,
    tryAgain: () => setEmail(null)
  }) : /*#__PURE__*/_jsx(FormScreen, {
    onSuccess: setEmail
  });
}